// src/components/FloatingPets.jsx
// 나비와 꽃이 화면 위를 유유히 떠다니는 컴포넌트
// CSS 애니메이션 충돌 방지: 모든 움직임을 JS requestAnimationFrame으로 통합
//
// [2026-04-15 꽃잎→꽃 교체] 대표님 결정 / 단비 기획서 v1.0 기반
// - petal 타입 완전 제거
// - 벚꽃(cherry_blossom) + 들꽃(wildflower_white/purple) placeholder 경로 세팅
// - 기획서 파라미터 반영 (FloatingPets 섹션 3.2)
//   spawnInterval 1~3s, spawnChance 0.3, velocity 30~60 px/s, swayAmplitude 15px,
//   swayFrequency 0.5Hz, rotation ±30°, scale 0.6~1.0, fadeOutHeight 80%

import { useEffect, useRef } from "react";

const GIF_BASE = "/assets/petmal/gif";
const FLOWER_BASE = "/assets/floating_pets/flowers"; // placeholder — 하린 에셋 수령 후 배치

// 기획서 파라미터 (단비 v1.0, 섹션 3.2)
const FLOWER_PARAMS = {
  spawnIntervalMin: 1000,   // ms
  spawnIntervalMax: 3000,   // ms
  spawnChance: 0.3,
  velocityMin: 30,          // px/s (위로)
  velocityMax: 60,          // px/s
  swayAmplitude: 15,        // px
  swayFrequency: 0.5,       // Hz
  rotationMin: -30,         // deg
  rotationMax: 30,          // deg
  rotationSpeedMax: 20,     // deg/s
  scaleMin: 0.6,
  scaleMax: 1.0,
  fadeOutHeightRatio: 0.8,  // 80%
};

// 상주(ambient) 아이템: 나비만 남김. 꽃잎은 제거.
const ITEMS = [
  { id: "b1", src: `${GIF_BASE}/butterfly_pink.gif`,   sz: 44, type: "butterfly" },
  { id: "b2", src: `${GIF_BASE}/butterfly_blue.gif`,    sz: 40, type: "butterfly" },
  { id: "b3", src: `${GIF_BASE}/butterfly_purple.gif`,  sz: 46, type: "butterfly" },
  { id: "ff1", src: `${GIF_BASE}/firefly_1.gif`,        sz: 24, type: "firefly" },
  { id: "ff2", src: `${GIF_BASE}/firefly_2.gif`,        sz: 22, type: "firefly" },
];

// 꽃 스폰 풀 — 기획서 3.3 에셋 폴더 구조 기반 (placeholder, 하린 제작 대기)
const FLOWER_ASSETS = [
  `${FLOWER_BASE}/cherry_blossom.png`,
  `${FLOWER_BASE}/wildflower_white.png`,
  `${FLOWER_BASE}/wildflower_purple.png`,
];

function rand(a, b) { return Math.random() * (b - a) + a; }

export default function FloatingPets() {
  const containerRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef(null);
  const spawnedFlowersRef = useRef([]); // 동적으로 스폰되는 꽃들

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const w = window.innerWidth;
    const h = window.innerHeight;
    const m = 40;

    // --- 상주 아이템 DOM 생성 ---
    const els = ITEMS.map((item) => {
      const span = document.createElement("span");
      span.style.cssText = `
        position:absolute; pointer-events:none; user-select:none;
        opacity:0; will-change:left,top,transform,opacity;
        filter:saturate(1.2);
      `;
      const img = document.createElement("img");
      img.src = item.src;
      img.alt = "";
      img.draggable = false;
      img.style.cssText = `width:${item.sz}px;height:${item.sz}px;object-fit:contain;`;
      span.appendChild(img);
      container.appendChild(span);
      return span;
    });

    const startTime = performance.now();
    stateRef.current = ITEMS.map((item, i) => {
      const angle = rand(0, Math.PI * 2);
      const spd = item.type === "butterfly" ? 0.6 : 0.25;
      return {
        el: els[i],
        type: item.type,
        sz: item.sz,
        x: rand(m, w - m),
        y: rand(m, h - m),
        vx: Math.cos(angle) * spd,
        vy: Math.sin(angle) * spd,
        wobbleOff: rand(0, 10000),
        rot: rand(0, 360),
        rotSpd: rand(-0.3, 0.3),
        flapPhase: rand(0, Math.PI * 2),
        facingR: Math.random() > 0.5,
        fadeDelay: i * 300,
      };
    });

    // --- 꽃 스폰 함수 (기획서 3.2 파라미터) ---
    function spawnFlower() {
      if (Math.random() > FLOWER_PARAMS.spawnChance) return;

      const ww = window.innerWidth;
      const hh = window.innerHeight;
      const src = FLOWER_ASSETS[Math.floor(Math.random() * FLOWER_ASSETS.length)];
      const scale = rand(FLOWER_PARAMS.scaleMin, FLOWER_PARAMS.scaleMax);
      const baseSz = 48;
      const sz = baseSz * scale;

      const span = document.createElement("span");
      span.style.cssText = `
        position:absolute; pointer-events:none; user-select:none;
        will-change:left,top,transform,opacity;
      `;
      const img = document.createElement("img");
      img.src = src;
      img.alt = "";
      img.draggable = false;
      img.style.cssText = `width:${sz}px;height:${sz}px;object-fit:contain;`;
      // placeholder 에셋 로드 실패 시 조용히 숨김 (하린 제작 전까지 에러 방지)
      img.onerror = () => { span.style.display = "none"; };
      span.appendChild(img);
      container.appendChild(span);

      const flower = {
        el: span,
        sz,
        x: rand(0, ww),                                                    // initialPosition x
        y: hh + 50,                                                        // y = -50 (아래에서 시작, 화면 기준 하단 +50)
        velocityY: -rand(FLOWER_PARAMS.velocityMin, FLOWER_PARAMS.velocityMax), // 위 방향 (음수)
        swayPhase: rand(0, Math.PI * 2),
        swayCenterX: 0,                                                    // 계산용
        rotation: rand(FLOWER_PARAMS.rotationMin, FLOWER_PARAMS.rotationMax),
        rotationSpeed: rand(-FLOWER_PARAMS.rotationSpeedMax, FLOWER_PARAMS.rotationSpeedMax),
        screenHeight: hh,
        spawnTime: performance.now(),
      };
      flower.swayCenterX = flower.x;
      spawnedFlowersRef.current.push(flower);
    }

    // --- 꽃 스폰 스케줄러 ---
    let spawnTimeout = null;
    function scheduleNextSpawn() {
      const delay = rand(FLOWER_PARAMS.spawnIntervalMin, FLOWER_PARAMS.spawnIntervalMax);
      spawnTimeout = setTimeout(() => {
        spawnFlower();
        scheduleNextSpawn();
      }, delay);
    }
    scheduleNextSpawn();

    let last = performance.now();

    function tick(now) {
      const dt = Math.min(now - last, 50);
      const dtSec = dt / 1000;
      last = now;
      const ww = window.innerWidth;
      const hh = window.innerHeight;
      const elapsed = now - startTime;

      // --- 상주 아이템 (나비 + 반딧불) ---
      stateRef.current.forEach((s) => {
        const fadeProgress = Math.min(1, Math.max(0, (elapsed - s.fadeDelay) / 2000));
        const opacity = fadeProgress * 0.75;

        if (s.type === "butterfly") {
          if (Math.random() < 0.008) {
            const a = rand(0, Math.PI * 2);
            s.vx = Math.cos(a) * 0.6;
            s.vy = Math.sin(a) * 0.6;
            s.facingR = s.vx > 0;
          }
          s.x += s.vx * dt * 0.06;
          s.y += s.vy * dt * 0.06;
          s.x += Math.sin((now + s.wobbleOff) * 0.002) * 0.3;
          s.y += Math.cos((now + s.wobbleOff * 1.3) * 0.003) * 0.2;

          if (s.y > hh + s.sz) { s.y = -s.sz * 2; s.x = rand(m, ww - m); }
          if (s.y < -s.sz * 2) { s.y = hh - m; s.x = rand(m, ww - m); }
          if (s.x < -s.sz) s.x = ww - m;
          if (s.x > ww + s.sz) s.x = m;

          s.flapPhase += dt * 0.015;
          const flap = 0.65 + 0.35 * Math.sin(s.flapPhase);
          const flip = s.facingR ? 1 : -1;
          const bob = Math.sin(now * 0.003 + s.wobbleOff) * 3;

          s.el.style.left = s.x + "px";
          s.el.style.top = (s.y + bob) + "px";
          s.el.style.transform = `scaleX(${flip * flap})`;
          s.el.style.opacity = opacity;

        } else if (s.type === "firefly") {
          // 반딧불: 느긋한 떠다님 + 반짝임
          if (Math.random() < 0.005) {
            const a = rand(0, Math.PI * 2);
            s.vx = Math.cos(a) * 0.3;
            s.vy = Math.sin(a) * 0.3;
          }
          s.x += s.vx * dt * 0.06;
          s.y += s.vy * dt * 0.06;
          s.x += Math.sin((now + s.wobbleOff) * 0.0008) * 0.15;

          if (s.y > hh + s.sz) { s.y = -s.sz * 2; s.x = rand(m, ww - m); }
          if (s.y < -s.sz * 2) { s.y = hh - m; s.x = rand(m, ww - m); }
          if (s.x < -s.sz) s.x = ww - m;
          if (s.x > ww + s.sz) s.x = m;

          const pulse = 0.8 + 0.4 * Math.sin(now * 0.003 + s.wobbleOff);
          s.el.style.left = s.x + "px";
          s.el.style.top = s.y + "px";
          s.el.style.transform = `scale(1)`;
          s.el.style.opacity = opacity * pulse;
        }
      });

      // --- 스폰된 꽃 업데이트 (기획서 3.2 파라미터) ---
      const remaining = [];
      for (const f of spawnedFlowersRef.current) {
        f.y += f.velocityY * dtSec;
        const swayOffset =
          Math.sin((now / 1000) * FLOWER_PARAMS.swayFrequency * 2 * Math.PI + f.swayPhase) *
          FLOWER_PARAMS.swayAmplitude;
        const drawX = f.swayCenterX + swayOffset;
        f.rotation += f.rotationSpeed * dtSec;

        // fadeOutHeight: 상단 80% 지점 위로 올라가면 페이드아웃
        const fadeThreshold = f.screenHeight * (1 - FLOWER_PARAMS.fadeOutHeightRatio);
        let opacity = 1;
        if (f.y < fadeThreshold) {
          opacity = Math.max(0, f.y / fadeThreshold);
        }

        f.el.style.left = drawX + "px";
        f.el.style.top = f.y + "px";
        f.el.style.transform = `rotate(${f.rotation}deg)`;
        f.el.style.opacity = opacity.toString();

        // 화면 위로 완전히 사라지면 제거
        if (f.y < -f.sz * 2 || opacity <= 0.01) {
          if (f.el.parentNode) f.el.parentNode.removeChild(f.el);
        } else {
          remaining.push(f);
        }
      }
      spawnedFlowersRef.current = remaining;

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (spawnTimeout) clearTimeout(spawnTimeout);
      els.forEach((el) => { if (el.parentNode) el.parentNode.removeChild(el); });
      spawnedFlowersRef.current.forEach((f) => {
        if (f.el.parentNode) f.el.parentNode.removeChild(f.el);
      });
      spawnedFlowersRef.current = [];
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: "fixed",
        top: 0, left: 0,
        width: "100vw", height: "100vh",
        zIndex: -1,
        pointerEvents: "none",
        overflow: "visible",
      }}
      aria-hidden="true"
    />
  );
}
